<template>
  <span
    ref="rootEl"
    class="slot-number"
    :aria-label="String(value ?? '')"
  >
    <span
      v-for="(ch, index) in chars"
      :key="colKey(ch, index)"
      class="slot-number__cell"
    >
      <span
        v-if="isDigit(ch)"
        class="slot-number__window"
        :style="windowStyle"
      >
        <span
          class="slot-number__reel"
          :ref="(el) => setReel(index, el)"
        >
          <span
            v-for="n in REEL_LEN"
            :key="n"
            class="slot-number__digit"
            :style="digitStyle"
          >{{ (n - 1) % 10 }}</span>
        </span>
      </span>
      <span
        v-else
        class="slot-number__symbol"
        :style="digitStyle"
      >{{ ch }}</span>
    </span>
  </span>
</template>

<script setup>
/**
 * 连贯老虎机：
 * - 全程 px + rAF，同一套 offset，不 remount、不压暗、不二次开转
 * - rolling 时匀速转；rolling 结束（或首次出现）从当前格减速到目标
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps({
  value: { type: [String, Number], default: '-' },
  rolling: { type: Boolean, default: false },
  spinKey: { type: Number, default: 0 },
  spins: { type: Number, default: 2 },
  durationMs: { type: Number, default: 1000 },
})

const REEL_LEN = 80
const ROLL_SPEED = 0.016 // 格 / ms

const rootEl = ref(null)
const chars = ref(Array.from(String(props.value ?? '-')))
const digitH = ref(0)
const prefersReduced = ref(false)

const reelEls = new Map()
const offsets = new Map()

let raf = 0
let lastTs = 0
let mode = 'idle' // idle | roll | settle
let settleFrom = new Map()
let settleTo = new Map()
let settleDuration = new Map() // 每列按时长匹配当前转速，避免衔接顿挫
let settleStart = 0
let mountedOnce = false

const windowStyle = computed(() => {
  const h = digitH.value
  if (!h) return { height: '1em', width: '0.65em' }
  return { height: `${h}px`, width: `${Math.max(12, Math.round(h * 0.62))}px` }
})

const digitStyle = computed(() => {
  const h = digitH.value
  if (!h) return { height: '1em', lineHeight: '1' }
  return { height: `${h}px`, lineHeight: `${h}px`, fontSize: `${h}px` }
})

function isDigit(ch) {
  return ch >= '0' && ch <= '9'
}

/** 同位置同类型复用 DOM，避免滚动中 remount 造成停顿 */
function colKey(ch, index) {
  return `${index}-${isDigit(ch) ? 'd' : 's'}`
}

function setReel(index, el) {
  if (el) reelEls.set(index, el)
  else reelEls.delete(index)
}

function measure() {
  const el = rootEl.value
  const px = el ? parseFloat(getComputedStyle(el).fontSize) || 0 : 0
  digitH.value = Math.max(12, Math.round(px || 40))
  return digitH.value
}

function paint(index, o) {
  const el = reelEls.get(index)
  const h = digitH.value
  if (!el || !h) return
  // 折到 [20, 60)，reel 共 80 格
  let x = o
  while (x >= 60) x -= 10
  while (x < 20) x += 10
  offsets.set(index, x)
  el.style.transform = `translate3d(0, ${-(x * h)}px, 0)`
}

function paintExact(index, o) {
  const el = reelEls.get(index)
  const h = digitH.value
  if (!el || !h) return
  offsets.set(index, o)
  el.style.transform = `translate3d(0, ${-(o * h)}px, 0)`
}

function stopRaf() {
  if (raf) cancelAnimationFrame(raf)
  raf = 0
  lastTs = 0
}

function tick(ts) {
  raf = 0
  if (!lastTs) lastTs = ts
  const dt = Math.min(34, ts - lastTs)
  lastTs = ts

  if (mode === 'roll') {
    chars.value.forEach((ch, i) => {
      if (!isDigit(ch)) return
      const speed = ROLL_SPEED * (1 + (i % 3) * 0.04)
      const cur = offsets.get(i) ?? 20
      paint(i, cur + speed * dt)
    })
    raf = requestAnimationFrame(tick)
    return
  }

  if (mode === 'settle') {
    let allDone = true
    settleTo.forEach((to, i) => {
      const from = settleFrom.get(i) ?? to
      const dur = settleDuration.get(i) ?? props.durationMs
      const t = Math.min(1, Math.max(0, (ts - settleStart) / dur))
      // 初速度接近匀速滚动：用 ease 使 t=0 导数更温和（smoothstep 变体）
      // f(t)=1-(1-t)^2 → f'(0)=2，再用时长把初速压到 ROLL_SPEED 附近
      const e = 1 - (1 - t) ** 2
      paintExact(i, from + (to - from) * e)
      if (t < 1) allDone = false
    })
    if (!allDone) {
      raf = requestAnimationFrame(tick)
      return
    }
    // 精确落到目标格，再折到中位（整圈差，肉眼无跳变）
    settleTo.forEach((to, i) => {
      const digit = ((Math.round(to) % 10) + 10) % 10
      paintExact(i, to)
      paint(i, 20 + digit)
    })
    mode = 'idle'
  }
}

function targetFrom(from, digit) {
  const pos = ((from % 10) + 10) % 10
  let forward = digit - pos
  if (forward < 0.001) forward += 10
  forward += Math.max(1, props.spins) * 10
  return from + forward
}

function startRoll() {
  if (prefersReduced.value) return
  stopRaf()
  measure()
  if (!chars.value.some(isDigit)) {
    chars.value = Array.from(String(props.value ?? '0.00'))
  }
  nextTick(() => {
    measure()
    chars.value.forEach((ch, i) => {
      if (!isDigit(ch)) return
      if (!offsets.has(i)) paint(i, 20 + Number(ch))
      else paint(i, offsets.get(i))
    })
    mode = 'roll'
    raf = requestAnimationFrame(tick)
  })
}

/**
 * @param {{ fromScratch?: boolean }} opts
 * fromScratch: 首次出现，没有滚动上下文时，先落到一个起点再减速到目标
 */
async function startSettle(opts = {}) {
  const { fromScratch = false } = opts
  stopRaf()

  const next = Array.from(String(props.value ?? '-'))
  if (prefersReduced.value) {
    chars.value = next
    mode = 'idle'
    await nextTick()
    measure()
    next.forEach((ch, i) => {
      if (!isDigit(ch)) return
      paint(i, 20 + Number(ch))
    })
    return
  }

  // 冻结当前 offset（改 chars 前）
  const frozen = new Map(offsets)

  // Vue 3.3 无 flushSync：nextTick 后立刻写回 transform，抢在浏览器绘制前恢复位置
  chars.value = next
  await nextTick()
  measure()

  settleFrom = new Map()
  settleTo = new Map()
  settleDuration = new Map()

  next.forEach((ch, i) => {
    if (!isDigit(ch)) return
    let from = frozen.has(i) ? frozen.get(i) : 20
    if (fromScratch && !frozen.has(i)) {
      from = 20 + ((i * 3) % 10)
    }
    const speed = ROLL_SPEED * (1 + (i % 3) * 0.04)
    const to = targetFrom(from, Number(ch))
    const distance = Math.max(0.001, to - from)
    // f(t)=1-(1-t)^2 的 f'(0)=2 → 初速 ≈ 2*distance/dur
    // 令初速 ≈ 当前匀速：dur ≈ 2*distance/speed
    const dur = fromScratch
      ? props.durationMs
      : Math.max(700, Math.min(1800, (2 * distance) / speed))
    settleFrom.set(i, from)
    settleTo.set(i, to)
    settleDuration.set(i, dur)
    paintExact(i, from)
  })

  ;[...offsets.keys()].forEach((i) => {
    if (i >= next.length || !isDigit(next[i])) offsets.delete(i)
  })

  mode = 'settle'
  settleStart = performance.now()
  raf = requestAnimationFrame(tick)
}

/** 日付切替など：アニメなしで最新値を表示 */
async function snapToValue() {
  stopRaf()
  mode = 'idle'
  const next = Array.from(String(props.value ?? '-'))
  chars.value = next
  await nextTick()
  measure()
  next.forEach((ch, i) => {
    if (!isDigit(ch)) return
    paint(i, 20 + Number(ch))
  })
  ;[...offsets.keys()].forEach((i) => {
    if (i >= next.length || !isDigit(next[i])) offsets.delete(i)
  })
}

onMounted(async () => {
  prefersReduced.value = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  await nextTick()
  measure()
  mountedOnce = true
  // 首次挂载（骨架结束后）のみスロット落位アニメ
  void startSettle({ fromScratch: true })
})

onUnmounted(() => stopRaf())

watch(
  () => String(props.value),
  () => {
    if (!mountedOnce) return
    // 首次落位アニメ中は触らない
    if (mode === 'settle') return
    if (mode === 'roll') {
      stopRaf()
      mode = 'idle'
    }
    void snapToValue()
  },
)
</script>

<style lang="less" scoped>
.slot-number {
  display: inline-flex;
  align-items: flex-end;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

.slot-number__cell {
  display: inline-flex;
  flex: none;
  align-items: flex-end;
  line-height: 1;
}

.slot-number__window {
  display: block;
  overflow: hidden;
  line-height: 1;
}

.slot-number__reel {
  display: block;
  will-change: transform;
}

.slot-number__digit {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  overflow: hidden;
  text-align: center;
}

.slot-number__symbol {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.04em;
}
</style>
